import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertCircle, IconArrowBack, IconMail, IconSend } from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { CustomerFeeApi } from "../../../apis";
import type { FeeSummaryResponse } from "../../../apis/customerFee";
import { PageLayout } from "../../../components/layout";
import { Message } from "../../../utils/message";
import { formatDate, formatMoney } from "../../../utils/format";
import { calcMethodLabel, feeBasisLabel, scopeLabel } from "../../../utils/fee-policy-labels";

const TYPE_LABEL: Record<string, string> = { BUYER: "Худалдан авагч", SUPPLIER: "Нийлүүлэгч" };
const typeLabel = (t: string | null) => (t ? TYPE_LABEL[t] ?? t : "—");
const pct = (rate: string | null | undefined) =>
  rate == null || rate === "" ? "—" : `${(Number(rate) * 100).toFixed(2)}%`;

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>
    </Group>
  );
}

const CustomerFeeDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useSWR(id ? `swr.my.customer-fee.${id}` : null, () =>
    id ? CustomerFeeApi.feeSummary(id) : null
  );
  const summary = data as FeeSummaryResponse | undefined;

  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");

  const email = summary?.customer?.email ?? null;
  const hasEmail = !!email;

  const onSend = async () => {
    if (!id) return;
    setSending(true);
    try {
      const res = await CustomerFeeApi.sendFeeInvoice(id);
      Message.success(`${res.email} рүү ${res.amount} ${res.currency}-ийн нэхэмжлэх илгээгдлээ.`);
      setSendOpen(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      Message.error(e?.message ?? "Илгээхэд алдаа гарлаа.");
    } finally {
      setSending(false);
    }
  };

  if (isLoading || !summary) {
    return (
      <PageLayout title="Харилцагчийн шимтгэл">
        <Stack gap="md">
          <Skeleton height={120} radius="md" />
          <Skeleton height={180} radius="md" />
        </Stack>
      </PageLayout>
    );
  }

  const { customer, policy, summary: totals, txns } = summary;
  const currency = totals.feeCurrency;

  return (
    <PageLayout
      title={customer.name}
      extra={[
        <Button
          key="back"
          variant="default"
          leftSection={<IconArrowBack size={16} />}
          onClick={() => navigate("/my-billing/fee-policy/customers")}>
          Харилцагчид
        </Button>,
        <Button
          key="send"
          leftSection={<IconSend size={16} />}
          disabled={!hasEmail}
          onClick={() => setSendOpen(true)}>
          Нэхэмжлэх илгээх
        </Button>,
      ]}>
      <Stack gap="lg">
        <Group gap="xs">
          <Badge color="brand" variant="light">{typeLabel(customer.type)}</Badge>
          {!hasEmail && (
            <Badge color="yellow" variant="light" leftSection={<IconAlertCircle size={12} />}>
              И-мэйл бүртгэнэ үү
            </Badge>
          )}
        </Group>

        {/* Fee hero card */}
        <Card withBorder padding="lg" radius="md" bg="light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))">
          <Stack gap="xs">
            <Text size="sm" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>
              Энэ үе төлөх ёстой шимтгэл
            </Text>
            <Group align="baseline" gap="sm">
              <Title order={1} c="brand.7" style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatMoney(totals.feeDue, currency)}
              </Title>
              <Badge color={totals.feeStatus === "PAYABLE" ? "yellow" : totals.feeStatus === "SETTLED" ? "green" : "gray"} variant="light">
                {totals.feeStatus === "PAYABLE" ? "Төлөгдөөгүй" : totals.feeStatus === "SETTLED" ? "Төлсөн" : "Хэсэгчилсэн"}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Орлого {formatMoney(totals.revenue, currency)} × {pct(totals.feeRate)} = шимтгэл {formatMoney(totals.feeDue, currency)}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mt="sm">
              <Box><Text size="xs" c="dimmed">Захиалга</Text><Text fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>{totals.orderCount}</Text></Box>
              <Box><Text size="xs" c="dimmed">Орлого</Text><Text fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(totals.revenue, currency)}</Text></Box>
              <Box><Text size="xs" c="dimmed">Хувь</Text><Text fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>{pct(totals.feeRate)}</Text></Box>
            </SimpleGrid>
          </Stack>
        </Card>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {/* Customer info */}
          <Card withBorder padding="md" radius="md">
            <Stack gap="sm">
              <Title order={5}>Харилцагчийн мэдээлэл</Title>
              <InfoRow label="Байгууллага" value={customer.name} />
              <InfoRow label="Төрөл" value={typeLabel(customer.type)} />
              <InfoRow label="Регистр" value={customer.regNumber ?? "—"} />
              <InfoRow label="И-мэйл" value={customer.email ?? "—"} />
              <InfoRow label="Утас" value={customer.phone ?? "—"} />
              <InfoRow label="Холбоо барих" value={customer.contact ?? "—"} />
            </Stack>
          </Card>

          {/* Assigned policy */}
          <Card withBorder padding="md" radius="md">
            <Stack gap="sm">
              <Title order={5}>Тохируулсан бодлого</Title>
              {policy ? (
                <>
                  <InfoRow label="Бодлого" value={`${scopeLabel(policy.scope)}${policy.name ? ` · ${policy.name}` : ""}`} />
                  <InfoRow label="Тооцоо" value={calcMethodLabel(policy.calculationMethod)} />
                  <InfoRow label="Хувь" value={pct(policy.feeRate)} />
                  <InfoRow label="Тооцох суурь" value={feeBasisLabel(policy.feeBasis)} />
                  <InfoRow label="Доод / Дээд" value={`${policy.minFeeAmount ? formatMoney(policy.minFeeAmount, currency) : "—"} / ${policy.maxFeeAmount ? formatMoney(policy.maxFeeAmount, currency) : "—"}`} />
                  <InfoRow label="Эхлэх огноо" value={policy.effectiveFrom ? formatDate(policy.effectiveFrom) : "—"} />
                </>
              ) : (
                <Text size="sm" c="dimmed">Идэвхтэй бодлого олдсонгүй.</Text>
              )}
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Orders → fee table */}
        <Card withBorder padding="md" radius="md">
          <Stack gap="sm">
            <Title order={5}>Захиалга ба шимтгэл</Title>
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Захиалга</Table.Th>
                    <Table.Th>Орлого</Table.Th>
                    <Table.Th>Хувь</Table.Th>
                    <Table.Th>Шимтгэл</Table.Th>
                    <Table.Th>Төлөв</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {txns.length === 0 ? (
                    <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="md">Шимтгэл алга.</Text></Table.Td></Table.Tr>
                  ) : (
                    txns.map((t) => (
                      <Table.Tr key={t.orderId}>
                        <Table.Td>{t.orderNumber}</Table.Td>
                        <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(t.baseAmount, t.feeCurrency)}</Table.Td>
                        <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{pct(t.appliedRate)}</Table.Td>
                        <Table.Td style={{ fontVariantNumeric: "tabular-nums" }}>{formatMoney(t.finalFeeAmount, t.feeCurrency)}</Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light" color={t.status === "SETTLED" ? "green" : t.status === "PENDING" ? "yellow" : "gray"}>
                            {t.status === "SETTLED" ? "Орлого орсон" : t.status === "PENDING" ? "Хүлээгдэж буй" : t.status}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        </Card>
      </Stack>

      <Modal opened={sendOpen} onClose={() => setSendOpen(false)} title="Нэхэмжлэх илгээх" centered>
        <Stack gap="md">
          {!hasEmail ? (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              Харилцагчийн и-мэйл бүртгэгдээгүй тул илгээх боломжгүй.
            </Alert>
          ) : (
            <>
              <Stack gap={4}>
                <InfoRow label="Харилцагч" value={customer.name} />
                <InfoRow label="Захиалгын тоо" value={totals.orderCount} />
                <InfoRow label="Нийт орлого" value={formatMoney(totals.revenue, currency)} />
                <InfoRow label="Төлөх шимтгэл" value={formatMoney(totals.feeDue, currency)} />
              </Stack>
              <TextInput label="Хүлээн авагч и-мэйл" value={email ?? ""} readOnly leftSection={<IconMail size={16} />} />
              <Textarea label="Тайлбар (заавал биш)" value={note} onChange={(e) => setNote(e.currentTarget.value)} autosize minRows={2} />
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setSendOpen(false)} disabled={sending}>Болих</Button>
                <Button leftSection={<IconSend size={16} />} onClick={onSend} loading={sending}>Илгээх</Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </PageLayout>
  );
};

export default CustomerFeeDetail;
